import React from 'react';
import styled from '@emotion/styled';
import { AUSTRALIA, MIN_ADDRESS_SEARCH_STRING } from 'utils/constants';
import { AddressDataType, useSearchAddress } from 'hooks/useSearchAddress';
import { useStructuredAddress } from 'hooks/useStructuredAddress';
import { isEmpty, get, debounce } from 'lodash';
import { UseFormReturn } from 'react-hook-form';
import { Popper, Paper, PopperProps } from '@mui/material';
import { Autocomplete } from '@qbe/ads-core';
import { FormDataType } from 'contexts/FormProvider';
import { Grid, TextField, Link } from '@qbe/ads-core';
import { useContent, useContentContext } from '@qbe/dfc-content';

type AddressType = 'residential' | 'physical' | 'postal';

interface ManualAddressFields {
  country: keyof FormDataType;
  streetAddress: keyof FormDataType;
  suburb: keyof FormDataType;
  state: keyof FormDataType;
  postCode: keyof FormDataType;
}
interface AddressAutocomplete {
  form: UseFormReturn<FormDataType>;
  fieldName: keyof FormDataType;
  fieldNameIsValid: keyof FormDataType;
  fieldNameIsManual: keyof FormDataType;
  linkType: AddressType;
  label: string;
  addressFieldNames: ManualAddressFields; // optional until all address field are done
  dataTrack?: string;
  dataTrackValue?: 'capture' | 'mask';
  isOptional?: boolean;
}

export const ManualText = styled.label`
  flex-grow: 1;
  margin-top: 24px;
  color: #0a1f44;
  height: 79px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  line-height: 18px;
  white-space: pre-wrap;
`;

export const ManualTextContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px;
  background-color: #f1f2f4;
`;

export const AutoCompleteManualText = styled.p`
  color: #0a1f44;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  letter-spacing: 0;
  line-height: 21px;
`;

const AddressAutocomplete = ({
  form,
  fieldName,
  fieldNameIsValid,
  fieldNameIsManual,
  label,
  linkType,
  addressFieldNames,
  dataTrack = fieldName || label,
  dataTrackValue,
  isOptional,
}: AddressAutocomplete) => {
  const {
    watch,
    setValue,
    setError,
    clearErrors: clearError,
    formState: { errors },
    trigger: triggerValidation,
    control,
  } = form;

  const {
    searchAddress,
    addressList,
    error: addressError,
    clearAddressList,
    searchValue,
    searching,
  } = useSearchAddress();

  const { getAddressData } = useStructuredAddress();

  const { getTextContent } = useContent({ context: useContentContext() });

  type FieldNameObject = Extract<AddressAutocomplete['fieldName'], `${string}Object`>;
  const fieldNameObject = (fieldName + 'Object') as FieldNameObject;
  const fieldObject = watch(fieldNameObject);

  React.useEffect(() => {
    const hasAddressError = !isOptional ? !isEmpty(addressError) : !isEmpty(addressError) && String(watch(fieldName));
    const validValue = !isEmpty(addressList) && String(watch(fieldName)).length >= MIN_ADDRESS_SEARCH_STRING;

    if (hasAddressError) {
      setError(fieldName, { type: 'invalid', message: getTextContent('addressAutocomplete.unavailable') });
    }

    if (validValue) {
      clearError(fieldName);
      if (!isEmpty(fieldObject)) {
        const { partialAddress } = fieldObject;
        if (partialAddress !== watch(fieldName)) {
          setError(fieldName, { type: 'invalid', message: getTextContent('addressAutocomplete.includeUnitNumber') });
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressError, fieldObject, addressList, clearError, fieldName, searchValue, searching, setError, isOptional]);

  const debouncedSearch = debounce(async (value: string) => {
    const isLengthInvalid = !isOptional
      ? !searching && value.length <= MIN_ADDRESS_SEARCH_STRING
      : !searching && value.length <= MIN_ADDRESS_SEARCH_STRING && value;

    const addressListData = await searchAddress({ searchTerm: value });

    if (isLengthInvalid || (Array.isArray(addressListData) && addressListData.length === 0)) {
      setError(fieldName, { type: 'invalid', message: getTextContent('addressAutocomplete.includeUnitNumber') });
    } else {
      clearError(fieldName);
    }
  }, 500);

  const handleChangeAddress = (e: React.ChangeEvent<object>, value: string) => {
    clearAddressList();

    if (isOptional && !value) {
      setValue(fieldName, '');
      setValue(fieldNameObject, null);

      setValue(addressFieldNames.country, '');
      setValue(addressFieldNames.streetAddress, '');
      setValue(addressFieldNames.suburb, '');
      setValue(addressFieldNames.state, '');
      setValue(addressFieldNames.postCode, '');
    }

    if (e) {
      if (e.type === 'change') {
        setValue(fieldNameIsValid, false);
        debouncedSearch(value);
      }
      setValue(fieldName, value);
      triggerValidation(fieldName);

      if (!isEmpty(fieldObject)) {
        const { partialAddress } = fieldObject;

        if (partialAddress === watch(fieldName)) {
          clearError(fieldName);
          setValue(fieldNameIsValid, true);
        }
      }
    }
  };

  const handleClickManualLink = () => {
    if (fieldObject === null) {
      setValue(addressFieldNames.country, AUSTRALIA);
      setValue(addressFieldNames.streetAddress, '');
      setValue(addressFieldNames.suburb, '');
      setValue(addressFieldNames.state, '');
      setValue(addressFieldNames.postCode, '');
      clearError(fieldNameIsManual);
    } else {
      triggerValidation(fieldNameIsManual);
      triggerValidation(addressFieldNames.country);
      triggerValidation(addressFieldNames.streetAddress);
      triggerValidation(addressFieldNames.suburb);
      triggerValidation(addressFieldNames.state);
      triggerValidation(addressFieldNames.postCode);
    }

    setValue(fieldName, '');
    setValue(fieldNameObject, null);
    setValue(fieldNameIsManual, true);
  };

  // Manual Address Link
  const displayAddressHelper = () => (
    <>
      <Grid component="span" container item justifyContent="center" xs={12}>
        {get(errors, `${fieldName}.message`)}
      </Grid>
      {getTextContent('addressAutocomplete.cantFindTheAddress')}&nbsp;
      <Link
        sx={{ cursor: 'pointer' }}
        component="a"
        data-test={`${linkType}-manual-link`}
        onClick={handleClickManualLink}
      >
        {getTextContent('addressAutocomplete.enterItManually')}
      </Link>
    </>
  );

  const CustomPaperComponent = (options: React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>) => (
    <Paper elevation={1}>
      {options.children}

      {!isEmpty(addressList) && (
        <ManualTextContainer>
          <AutoCompleteManualText>
            {getTextContent('addressAutocomplete.cantFindTheAddress')}{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              component="a"
              data-test={`${linkType}-manual-link-autocomplete`}
              onMouseDown={handleClickManualLink}
              underline="always"
            >
              {getTextContent('addressAutocomplete.enterItManually')}
            </Link>
          </AutoCompleteManualText>
        </ManualTextContainer>
      )}
    </Paper>
  );

  const CustomPopperComponent = ({ style, ...props }: PopperProps) =>
    !isEmpty(addressList) ? <Popper {...props} style={{ ...style, height: 0 }} placement="bottom" /> : null;

  return (
    <Autocomplete
      isOptionEqualToValue={(option, value) => option.moniker === value.moniker}
      options={addressList || []}
      onFocus={() => {
        if (watch(fieldName) && !isEmpty(addressList)) {
          clearError(fieldName);
        }
      }}
      getOptionLabel={(option) => option.partialAddress || ''}
      onInputChange={handleChangeAddress}
      value={fieldObject as (Partial<AddressDataType> & string) | undefined}
      filterOptions={(options) => options}
      PaperComponent={CustomPaperComponent}
      PopperComponent={CustomPopperComponent as any}
      onChange={async (_, selectedValue) => {
        setValue(fieldNameObject, selectedValue as AddressDataType);
        if (!!selectedValue) {
          const data = await getAddressData(selectedValue.moniker);
          if (data) {
            const { buildingName, streetAddress1, city, stateCode, postalCode, country } = data;
            setValue(fieldNameIsValid, true);
            setValue(addressFieldNames.country, country);
            setValue(
              addressFieldNames.streetAddress,
              [buildingName, streetAddress1].filter((str) => str.length).join(', ')
            );
            setValue(addressFieldNames.suburb, city);
            setValue(addressFieldNames.state, stateCode);
            setValue(addressFieldNames.postCode, postalCode);
          }
        }

        return selectedValue;
      }}
      onBlur={() => {
        const isFieldInvalid = !isOptional
          ? !watch(fieldNameIsValid)
          : !watch(fieldNameIsValid) && String(watch(fieldName));

        if (isFieldInvalid && isEmpty(addressError)) {
          setError(fieldName, { type: 'invalid', message: getTextContent('addressAutocomplete.includeUnitNumber') });
        } else if (isFieldInvalid && !isEmpty(addressError)) {
          setError(fieldName, { type: 'invalid', message: getTextContent('addressAutocomplete.unavailable') });
        }

        if (!isEmpty(fieldObject)) {
          const { partialAddress } = fieldObject;

          if (partialAddress === watch(fieldName)) {
            clearError(fieldName);
            setValue(fieldNameIsValid, true);
          }
        }
      }}
      data-track={dataTrack}
      data-track-value={dataTrackValue}
      renderInput={(params) => (
        <TextField
          {...params}
          name={fieldName}
          error={!!get(errors, fieldName)}
          label={label}
          helperText={displayAddressHelper()}
        />
      )}
    />
  );
};

export default AddressAutocomplete;
